import { Pencil, Trash2, Calendar, CheckCircle, XCircle } from 'lucide-react';
import type { TimeException } from '../../types/timeCondition';
import { MONTHS } from '../../types/timeCondition';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeader,
  TableRow,
  Button,
  EmptyState,
  Badge,
} from '../ui';

interface ExceptionListProps {
  exceptions: TimeException[];
  onEdit: (exception: TimeException) => void;
  onDelete: (exception: TimeException) => void;
  isLoading?: boolean;
}

function ExceptionList({ exceptions, onEdit, onDelete, isLoading }: ExceptionListProps) {
  const getMonthName = (month: number) => {
    return MONTHS.find(m => m.value === month)?.label || 'Desconhecido';
  };

  const formatDate = (exception: TimeException) => {
    const day = exception.exception_day;
    const month = getMonthName(exception.exception_month);
    const year = exception.exception_year;

    return year ? `${day} de ${month} de ${year}` : `${day} de ${month} (anual)`;
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (exceptions.length === 0) {
    return (
      <EmptyState
        icon={<Calendar className="h-12 w-12" />}
        title="Nenhuma exceção cadastrada"
        description="Adicione exceções como feriados e datas especiais"
      />
    );
  }

  return (
    <TableContainer>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Data</TableHead>
            <TableHead>Descrição</TableHead>
            <TableHead>Status</TableHead>
            <TableHead className="relative">
              <span className="sr-only">Ações</span>
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {exceptions.map(exception => (
            <TableRow key={exception.id}>
              <TableCell>{formatDate(exception)}</TableCell>
              <TableCell>{exception.description}</TableCell>
              <TableCell>
                <Badge
                  variant={exception.is_available ? 'success' : 'error'}
                  className="flex items-center gap-1"
                >
                  {exception.is_available ? (
                    <>
                      <CheckCircle className="h-3 w-3" />
                      Disponível
                    </>
                  ) : (
                    <>
                      <XCircle className="h-3 w-3" />
                      Indisponível
                    </>
                  )}
                </Badge>
              </TableCell>
              <TableCell className="text-right space-x-2">
                <Button variant="ghost" onClick={() => onEdit(exception)} title="Editar">
                  <Pencil className="h-4 w-4" />
                </Button>
                <Button variant="ghost" onClick={() => onDelete(exception)} title="Excluir">
                  <Trash2 className="h-4 w-4 text-red-600" />
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export default ExceptionList;
